// Music manager singleton - manages background music playback based on game state

const TRACKS = {
  title: '/assets/audio/324_Distilled_Highlands.mp3',
  'waiting-room': '/assets/audio/177_Tavern_Music.mp3',
  exploration: '/assets/audio/62_Middle_Earth_Dawn.mp3',
  combat: '/assets/audio/465_Light_the_Beacons.mp3',
};

const FADE_DURATION_MS = 1500;
const FADE_INTERVAL_MS = 50;
const VOLUME_KEY = 'd3-music-volume';
const MUTED_KEY = 'd3-music-muted';

class MusicManager {
  constructor() {
    /** @type {string} */
    this._currentState = 'silent';
    /** @type {number} */
    this._volume = this._loadVolume();
    /** @type {boolean} */
    this._muted = this._loadMuted();
    /** @type {Map<string, HTMLAudioElement>} */
    this._audioElements = new Map();
    /** @type {number|undefined} */
    this._fadeInterval = undefined;
    /** @type {boolean} */
    this._playbackDeferred = false;

    this._initAudioElements();
    this._setupInteractionListener();
  }

  _setupInteractionListener() {
    const handler = () => {
      if (this._playbackDeferred && this._currentState !== 'silent') {
        this._playbackDeferred = false;
        const audio = this._audioElements.get(this._currentState);
        if (audio) {
          this._fadeIn(audio);
        }
      }
      document.removeEventListener('click', handler);
      document.removeEventListener('keydown', handler);
    };
    document.addEventListener('click', handler);
    document.addEventListener('keydown', handler);
  }

  _initAudioElements() {
    for (const [state, src] of Object.entries(TRACKS)) {
      const audio = new Audio(src);
      audio.loop = true;
      audio.preload = 'auto';
      audio.volume = 0;
      this._audioElements.set(state, audio);
    }
  }

  /**
   * @returns {number}
   */
  _loadVolume() {
    const saved = localStorage.getItem(VOLUME_KEY);
    if (saved !== null) {
      const val = parseFloat(saved);
      if (!Number.isNaN(val) && val >= 0 && val <= 1) return val;
    }
    return 0.5;
  }

  /**
   * @returns {boolean}
   */
  _loadMuted() {
    return localStorage.getItem(MUTED_KEY) === 'true';
  }

  /**
   * @returns {number}
   */
  getVolume() {
    return this._volume;
  }

  /**
   * @returns {boolean}
   */
  isMuted() {
    return this._muted;
  }

  /**
   * @param {number} level - Volume from 0 to 1
   */
  setVolume(level) {
    this._volume = Math.max(0, Math.min(1, level));
    localStorage.setItem(VOLUME_KEY, String(this._volume));

    // Update the currently playing track's volume immediately
    if (!this._muted && this._currentState !== 'silent') {
      const audio = this._audioElements.get(this._currentState);
      if (audio) {
        audio.volume = this._volume;
      }
    }
  }

  /**
   * @param {boolean} muted
   */
  setMuted(muted) {
    this._muted = muted;
    localStorage.setItem(MUTED_KEY, String(muted));

    if (this._currentState !== 'silent') {
      const audio = this._audioElements.get(this._currentState);
      if (audio) {
        if (muted) {
          audio.pause();
        } else {
          audio.volume = this._volume;
          if (audio.paused) {
            audio.play().catch(() => {});
          }
        }
      }
    }
  }

  /** Pause all audio for app backgrounding. */
  pauseAll() {
    this._pausedAudioStates = [];
    for (const [state, audio] of this._audioElements.entries()) {
      if (!audio.paused) {
        this._pausedAudioStates.push(state);
        audio.pause();
      }
    }
  }

  /** Resume playback after app foregrounding. */
  resumeIfPlaying() {
    if (!this._pausedAudioStates?.length) return;
    const toResume = this._pausedAudioStates;
    this._pausedAudioStates = [];
    for (const state of toResume) {
      if (state !== this._currentState) continue;
      const audio = this._audioElements.get(state);
      if (audio) {
        audio.play().catch(() => {});
      }
    }
  }

  /**
   * Switch to a new music track with crossfade.
   * Calling with the same state as current is a no-op.
   * @param {'title'|'waiting-room'|'exploration'|'combat'|'silent'} state
   */
  switchTrack(state) {
    if (state === this._currentState) return;

    const previousState = this._currentState;
    this._currentState = state;

    // Cancel any in-progress fade
    if (this._fadeInterval !== undefined) {
      clearInterval(this._fadeInterval);
      this._fadeInterval = undefined;
    }

    // Fade out previous track
    const prevAudio = this._audioElements.get(previousState);
    if (prevAudio) {
      this._fadeOut(prevAudio);
    }

    // Fade in new track (unless going silent)
    if (state === 'silent') return;

    const nextAudio = this._audioElements.get(state);
    if (!nextAudio) return;

    this._fadeIn(nextAudio);
  }

  /**
   * @param {HTMLAudioElement} audio
   */
  _fadeOut(audio) {
    const startVolume = audio.volume;
    if (startVolume === 0) {
      audio.pause();
      audio.currentTime = 0;
      return;
    }

    const steps = FADE_DURATION_MS / FADE_INTERVAL_MS;
    const volumeStep = startVolume / steps;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      const newVolume = startVolume - volumeStep * currentStep;
      audio.volume = Math.max(0, newVolume);

      if (currentStep >= steps) {
        clearInterval(interval);
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
      }
    }, FADE_INTERVAL_MS);
  }

  /**
   * @param {HTMLAudioElement} audio
   */
  _fadeIn(audio) {
    if (this._muted) {
      // Don't start playback when muted — avoids iOS keeping an active
      // audio session at volume 0 which prevents background silence.
      this._playbackDeferred = true;
      return;
    }

    const targetVolume = this._volume;
    audio.volume = 0;
    audio.play().catch(() => {
      // Browser autoplay policy blocked playback — retry on next user interaction
      this._playbackDeferred = true;
    });

    const steps = FADE_DURATION_MS / FADE_INTERVAL_MS;
    const volumeStep = targetVolume / steps;
    let currentStep = 0;

    this._fadeInterval = setInterval(() => {
      currentStep++;
      audio.volume = Math.min(targetVolume, volumeStep * currentStep);

      if (currentStep >= steps) {
        clearInterval(this._fadeInterval);
        this._fadeInterval = undefined;
      }
    }, FADE_INTERVAL_MS);
  }
}

export const musicManager = new MusicManager();
